import moment from 'moment-timezone';

import { SCHEDULE_TYPES } from '@src/constants/commons';
import { DateUtil } from '@src/packages/utils/dates';

import type {
  TimelineEventColor,
  TimelineEventData,
  TimelineEventTimestamp,
  TimelineEventTrigger,
} from '../TimelineEvent.types';

const DEFAULT_COLOR: Required<TimelineEventColor> = { r: 128, g: 128, b: 128, a: 1 };
export const CHECKPOINT_ROUTE_COLOR = '#3f8dd8';

export const formatEventDateTime = (value: TimelineEventTimestamp): string =>
  moment(DateUtil.getTimestampMs(value) ?? 0).format('DD/MM/YY - HH:mm');

/** Drawer datetime as in Figma: `27/05/26 · 22:05 hs`. */
export const formatEventDrawerDateTime = (value: TimelineEventTimestamp): string =>
  moment(DateUtil.getTimestampMs(value) ?? 0).format('DD/MM/YY · HH:mm [hs]');

export const getActorName = (event: TimelineEventData): string => {
  const firstName = event.user?.firstName?.trim() ?? '';
  const lastName = event.user?.lastName?.trim() ?? '';
  const fullName = `${firstName} ${lastName}`.trim();
  if (fullName) {
    return fullName;
  }
  return event.user?.displayName?.trim() ?? '';
};

export const getDivisionName = (event: TimelineEventData): string =>
  event.division?.name?.trim() || event.execution?.division?.name?.trim() || '';

export const getEventIconColor = (event: TimelineEventData, isCheckpointRoute: boolean): string => {
  if (isCheckpointRoute) {
    return CHECKPOINT_ROUTE_COLOR;
  }
  const color = event.eventType?.color;
  return `rgba(${color?.r ?? DEFAULT_COLOR.r}, ${color?.g ?? DEFAULT_COLOR.g}, ${color?.b ?? DEFAULT_COLOR.b}, ${color?.a ?? DEFAULT_COLOR.a})`;
};

export const isCheckpointRouteEvent = (event: TimelineEventData): boolean =>
  event.execution?.schedule?.type === SCHEDULE_TYPES.checkpoint_route;

export const getTargetName = (event: TimelineEventData, isCheckpointRoute: boolean): string => {
  if (isCheckpointRoute) {
    return event.locations?.[0]?.name ?? '';
  }
  return event.trigger?.name?.trim() || event.eventType?.name?.trim() || '';
};

export const getContextSegments = (
  event: TimelineEventData,
  isCheckpointRoute: boolean,
  trigger?: TimelineEventTrigger | null,
): string[] => {
  const divisionName = getDivisionName(event);
  const scheduleName = event.execution?.schedule?.name?.trim();
  const targetName = isCheckpointRoute
    ? event.locations?.[0]?.name?.trim()
    : trigger?.name?.trim() || getTargetName(event, false);

  return [divisionName, scheduleName, targetName].filter((segment): segment is string =>
    Boolean(segment),
  );
};
